/**
 * Project Chat, re-backed onto the DynamoDB chat service.
 *
 * Owner, 2026-08-16: move the WHOLE of Project Chat onto DynamoDB with the UI and every
 * consumer signature byte-identical. This is the swap layer — the same operations the
 * edge relay served (list / create / send), but spoken to the Bearer-JWT chat client
 * (chatv1/transport.ts + chatv1/ws.ts), with the backend's shapes mapped back into the
 * ProjectRow / ProjectMessage the panel already renders.
 *
 * Gated behind {@link projectOnDynamo}. The old edge keeps serving until the flag flips,
 * so nothing regresses mid-build; flipping the flag IS the all-together cutover.
 */
import {
  listConversations,
  createConversation,
  getMessages,
  getMembers,
  addMember,
  leaveConversation,
  listAgents,
  addAgent,
  removeAgent,
  setAgentModeReq,
  mintUpload,
  attachmentUrl,
  Chatv1Error,
  type ConversationRow,
  type UploadMint,
  type WireMessage,
} from '../chatv1/transport'
import { connectChatv1, sendChatv1, sendChatv1Await } from '../chatv1/ws'
import { projectEcho } from './projectEcho'
import { chatv1SelfUser, chatv1SelfEmail } from '../chatv1/auth'
import { getAgentsEverywhere, type RosterMachine } from '../agents/everywhere'
import type { ProjectRow, ProjectMessage, ProjectMember, AttachableAgent, AgentMode } from '../transport/api'
import { inertBlob } from '../ui/inertBlob'

/*
 * THE CUTOVER SWITCH IS GONE, and this note is where it was.
 *
 * `projectOnDynamo()` read `localStorage.av_project_dynamo` and answered which of two
 * Project Chat backends the app should use; `seedProjectDynamoFlagFromUrl()` let the
 * owner flip it from a phone by visiting `?chat=dynamo` / `?chat=edge`. Between them
 * they gated 52 call sites across 11 files.
 *
 * The owner retired the edge lane on 2026-09-02 (Project Chat 舊的路徑完全刪了), so there
 * is one backend and nothing to switch. A stored `av_project_dynamo` key on an old
 * browser is now inert — deliberately not migrated or cleared, because reading it would
 * be the first line of a second switch.
 */

/**
 * ConversationRow (kind:project) → the ProjectRow the sidebar card renders. Optional fields
 * are assigned only when present: with exactOptionalPropertyTypes a missing unread must be
 * ABSENT, not `undefined`, so the card shows no badge rather than a zero.
 */
function convRowToProjectRow(c: ConversationRow): ProjectRow {
  const row: ProjectRow = { project_id: c.conv_id }
  if (c.title !== undefined) row.name = c.title
  // The backend computes a bounded unread already (transport.ts:84).
  if (c.unread !== undefined) row.unread = c.unread
  // The room's newest seq — mark-read advances the read cursor to here (the WS `read`
  // action needs this high-water; see dynamoProjectMarkRead). Carried on every project row.
  if (typeof c.last_seq === 'number') row.last_seq = c.last_seq
  // Sidebar card extras — the list now carries them for project rows (chat listConversations).
  if (c.member_count !== undefined) row.member_count = c.member_count
  if (c.agent_count !== undefined) row.agent_count = c.agent_count
  if (c.last_msg_preview) row.last_msg_preview = c.last_msg_preview
  if (c.last_from) row.last_from = c.last_from
  if (c.last_from_hash) row.last_from_hash = c.last_from_hash
  if (c.last_from_email) row.last_from_email = c.last_from_email
  if (c.last_from_picture) row.last_from_picture = c.last_from_picture
  if (c.group_id) row.group_id = c.group_id
  return row
}

/**
 * WireMessage → ProjectMessage. Agents are a later slice, so a message here is a human's:
 * the name is the server-enriched `sender_name`, and `from_ref.email` is set to the
 * VIEWER's own email only on their own rows (sender === self) — which is exactly what the
 * panel's "you" test keys on, so mine read "you" and everyone else reads "user".
 */
/** A reply's thread fields as the transcript keeps them (chat/Thread.tsx), from a message or an ack. */
export function threadFields(m: {
  reply_to?: number
  reply_to_sender?: string
  reply_to_text?: string
  reply_to_agent?: string
  reply_to_agent_name?: string | null
}): Record<string, unknown> {
  return {
    reply_to: m.reply_to,
    reply_to_sender: m.reply_to_sender ?? '',
    reply_to_text: m.reply_to_text ?? '',
    ...(m.reply_to_agent ? { reply_to_agent: m.reply_to_agent, reply_to_agent_name: m.reply_to_agent_name ?? '' } : {}),
  }
}

export function wireToProjectMessage(m: WireMessage, selfEmail: string | null): ProjectMessage {
  const mine = m.sender === chatv1SelfUser()
  const from_ref: { email?: string; agent?: string; label?: string; picture?: string } = {}
  if (m.via_agent) {
    // An AGENT spoke: render ITS identity (pixelpet seeded on the hash + agent name),
    // NOT the owner it speaks for (owner: agent push 應該用 agent 的 identity, 而非 owner).
    // from_ref.agent drives the PixelPet + "agent" badge; the owner still shows as
    // "owned by" via the sender's name.
    from_ref.agent = m.via_agent
    from_ref.label = m.via_agent_name || m.via_agent.slice(0, 8)
    if (m.sender_name) from_ref.email = m.sender_name
  } else {
    if (m.sender_name) from_ref.label = m.sender_name
    // The server enriches every row with the sender's Google photo — carry it so the
    // transcript shows faces, not just initials (owner: 頭像消失了). Absent ⇒ Avatar tiles.
    if (m.sender_picture) from_ref.picture = m.sender_picture
    if (mine && selfEmail) from_ref.email = selfEmail
  }
  return {
    message_id: m.msg_id,
    cursor: m.seq,
    ts: m.ts,
    body: m.envelope?.body ?? '',
    from_ref,
    // Mine, or one of my agents' (it speaks as me): what a group's posting limit counts (groups/GroupQuotaBar).
    ...(mine ? { mine: true } : {}),
    // A reply: the thread it hangs under (chat/Thread.tsx).
    ...(typeof m.reply_to === 'number' ? threadFields(m) : {}),
    // Sent from a whiteboard's chat: the board it names (board/BoardChat.tsx).
    ...(typeof m.board === 'string' ? { board: m.board, board_name: m.board_name || 'Whiteboard' } : {}),
    // …about an area of that board (board/area.ts).
    ...(typeof m.board === 'string' && m.area && typeof m.area === 'object' ? { area: m.area } : {}),
    // Sent from a page's chat: the page it names (page/PageScreen.tsx).
    ...(typeof m.page === 'string' ? { page: m.page, page_name: m.page_name || 'Untitled' } : {}),
  }
}

/**
 * GET /conversations?kind=project → {ok, projects} in the shape getProjects returned.
 * `left`/`archived` rows are dropped: a project you have left is not in your list.
 */
export async function dynamoGetProjects(): Promise<{ ok: boolean; projects: ProjectRow[] }> {
  const { conversations } = await listConversations('project')
  const projects = conversations
    .filter((c) => c.kind === 'project' && c.status !== 'left' && c.status !== 'archived')
    .map(convRowToProjectRow)
  return { ok: true, projects }
}

/**
 * The newest page of one room, in the shape `getProjectHistory` returned.
 *
 * WHY THIS WAS MISSING, and what it cost — owner, 2026-08-22: 在 Activity Tab 的彈出
 * 視窗裡面現在所有 project 的未讀訊息都沒有辦法顯示在裡面.
 *
 * The Project Chat cutover routed list / create / send / members / files through here
 * and left `getProjectHistory` pointed at the edge. Nothing complained, because the
 * only reader that still called it was the Activity feed — and the feed was ALSO
 * gated behind the machine vault, so it never got as far as asking. Two faults
 * hiding each other: fix either alone and the panel stays empty.
 *
 * `after` IS REFUSED, not translated. The edge's `after` pages FORWARD from a cursor;
 * this service pages BACKWARD from `before_seq`. There is no honest mapping between
 * them, and quietly answering with the wrong end of the history is the kind of
 * "working" that gets found weeks later. Forward reconcile on this backend is the
 * socket's `backfill` frame (useProjectMessages), not a REST page.
 */
export async function dynamoGetProjectHistory(
  projectId: string,
  after: number | undefined,
  limit?: number,
): Promise<{
  ok: boolean
  cursor: number
  messages: ProjectMessage[]
  has_more?: boolean
  oldest?: number
}> {
  if (after !== undefined) {
    throw new Error('project history: forward paging (`after`) is the edge path only')
  }
  const want = limit ?? 50
  const { messages } = await getMessages(projectId, undefined, want)
  const selfEmail = chatv1SelfEmail() || null
  const rows = messages
    .map((m) => wireToProjectMessage(m, selfEmail))
    // Display order, like the edge's own history branch: it takes the newest N by seq
    // and sorts them back. Consumers merge and re-sort anyway, but a list that arrives
    // in a stated order is one fewer thing for the next reader to discover.
    .sort((a, b) => a.ts - b.ts || a.cursor - b.cursor)
  return {
    ok: true,
    cursor: rows.length ? rows[rows.length - 1]!.cursor : 0,
    messages: rows,
    // A full page means there is probably more behind it; the caller pages with
    // `before_seq`, which is what `oldest` is for.
    has_more: rows.length >= want,
    ...(rows.length ? { oldest: rows[0]!.cursor } : {}),
  }
}

/**
 * Advance this human's read cursor to `seq` — the WebSocket `read` action (wsCursors.ts).
 * `seq` is the room's newest, read from the same sidebar row that showed the unread count
 * (ProjectRow.last_seq), so it clears exactly what that list poll surfaced.
 *
 * Fire-and-forget by design: the server upsert is monotonic (never lowers the cursor) and
 * unacked, and frames queue + flush on connect (ws.ts), so the write survives a socket
 * blip. There is thus nothing to await — the caller clears the badge optimistically, since
 * an immediate refetch would race this write and re-light the stale count. A missing/zero
 * seq is a no-op (an empty room has nothing to mark).
 */
export async function dynamoProjectMarkRead(
  projectId: string,
  seq?: number,
): Promise<{ ok?: boolean; project_id?: string; read_through?: number; unread?: number }> {
  const result: { ok: boolean; project_id: string; unread: number; read_through?: number } = {
    ok: true,
    project_id: projectId,
    unread: 0,
  }
  if (typeof seq === 'number' && seq > 0) {
    connectChatv1()
    sendChatv1({ action: 'read', conv_id: projectId, seq })
    result.read_through = seq
  }
  return result
}

/** POST /conversations {kind:project,title} → {ok, project} like createProject. */
export async function dynamoCreateProject(
  name: string,
): Promise<{ ok: boolean; project: ProjectRow; error?: string }> {
  const { conversation } = await createConversation({ kind: 'project', title: name.trim() })
  const project: ProjectRow = { project_id: conversation.conv_id }
  if (conversation.title !== undefined) project.name = conversation.title
  return { ok: true, project }
}

/**
 * Open (create-or-resolve) the MANAGED friend DM for `peerEmail` — a `kind:'dm'`
 * conversation on the chat service. Friendship is enforced SERVER-SIDE (403 `not_friends`
 * / 404 `unknown_peer`); the conv_id is DERIVED from the sorted pair, so this is idempotent
 * — the same two friends always resolve to the same room, whether it existed or not. This
 * is the managed rail that replaces the E2E relay for friend chat: the returned conv_id is
 * what ProjectPanel (rendered `locked`) + the managed transcript key on, and it carries the
 * full agent lane. Throws {@link Chatv1Error} on refusal.
 */
export async function dynamoOpenFriendDm(peerEmail: string, account?: string | null): Promise<{ conv_id: string }> {
  // `account` says WHICH friend when the address is two of them (owner, 2026-10-31: friends with
  // both accounts at one address, every DM by address was refused as unknown_peer).
  const { conversation } = await createConversation({
    kind: 'dm',
    peer: peerEmail,
    ...(account ? { peer_account: account } : {}),
  })
  return { conv_id: conversation.conv_id }
}

/**
 * Send over the WebSocket. There is no synchronous stored row (the edge returned one);
 * instead we hand back an optimistic PENDING row carrying the client-minted `msg_id`, so
 * the existing App → projectEcho path shows it at once and the `message` frame that echoes
 * back (same msg_id) reconciles it to sent inside the hook.
 *
 * `cursor` is a max sentinel so the row sorts to the bottom until its real seq lands; the
 * hook must exclude `status === 'pending'` rows from its backfill high-water mark.
 */
export async function dynamoSendProjectMessage(
  projectId: string,
  message: string,
  fromEmail?: string | null,
  fromName?: string | null,
  fromPicture?: string | null,
  /** The seq of the top-level message this answers (a reply in a group's chat; chat/Thread.tsx). */
  replyTo?: number,
  /** Sent from a whiteboard's chat: that board, which the message names to the room (board/BoardChat.tsx). */
  board?: { id: string; name: string },
  /** Sent from a page's chat: that page, as a board's (page/PageScreen.tsx). */
  page?: { id: string; name: string },
): Promise<{ ok: boolean; message?: Record<string, unknown> }> {
  const msgId = crypto.randomUUID()
  sendChatv1({
    action: 'send',
    conv_id: projectId,
    msg_id: msgId,
    envelope: { v: 1, mode: 'managed', content_type: 'text/plain', body: message },
    ...(replyTo ? { reply_to: replyTo } : {}),
    ...(board ? { board: board.id } : {}),
    ...(page ? { page: page.id } : {}),
  })
  // The optimistic row carries the SELF's identity (name + Google photo), not just the
  // email — the server never echoes a `message` frame back to the sender (fanout skips
  // the originating socket) and the `ack` carries no profile, so this row is the only
  // place the sender's own name/face comes from until a later reconcile re-fetches it.
  const from_ref: { email?: string; label?: string; picture?: string } = {}
  if (fromEmail) from_ref.email = fromEmail
  const label = fromName || fromEmail
  if (label) from_ref.label = label
  if (fromPicture) from_ref.picture = fromPicture
  const pending: ProjectMessage = {
    message_id: msgId,
    cursor: Number.MAX_SAFE_INTEGER,
    ts: Date.now() / 1000,
    body: message,
    from_ref,
    status: 'pending',
    mine: true,
    ...(replyTo ? { reply_to: replyTo } : {}),
    ...(board ? { board: board.id, board_name: board.name } : {}),
    ...(page ? { page: page.id, page_name: page.name } : {}),
    project_id: projectId,
  }
  return { ok: true, message: pending }
}

/**
 * A QUESTION ABOUT AN AREA OF A WHITEBOARD (board/area.ts; owner, 2026-10-31): the area's picture goes up first, the way
 * a shared file does (minted, POSTed straight to S3), then the message — naming the board, the area and the picture —
 * is sent and AWAITED, so the window that asked can say it went or why not. The service claims the picture with the
 * message, and only once S3 has said it arrived: until then the send is `attachment_invalid` and is tried again, as a
 * file's is. A picture that will not upload is left behind; the question still goes, with the items it names.
 */
export async function dynamoSendAreaQuestion(
  projectId: string,
  text: string,
  board: { id: string; name: string },
  area: { x: number; y: number; w: number; h: number; ids: string[] },
  picture: Blob | null,
  from: { email?: string | null; name?: string | null; picture?: string | null },
): Promise<ProjectMessage> {
  let shot: { s3_key: string; size: number; content_type: string } | null = null
  if (picture) {
    try {
      const mint = await mintUpload(projectId, picture.size, 'image/png')
      const up = await postToS3(mint, picture, 'area.png')
      if (up.ok) shot = { s3_key: mint.s3_key, size: picture.size, content_type: 'image/png' }
    } catch {
      /* the question goes without its picture */
    }
  }
  const envelope = {
    v: 1,
    mode: 'managed' as const,
    content_type: 'text/plain',
    body: text,
    ...(shot ? { attachments: [shot] } : {}),
  }
  const sentArea = { ...area, ...(shot ? { shot: shot.s3_key } : {}) }
  for (let attempt = 0; ; attempt++) {
    const msgId = crypto.randomUUID()
    try {
      const { seq, ts } = await sendChatv1Await({ action: 'send', conv_id: projectId, msg_id: msgId, envelope, board: board.id, area: sentArea })
      const from_ref: { email?: string; label?: string; picture?: string } = {}
      if (from.email) from_ref.email = from.email
      const label = from.name || from.email
      if (label) from_ref.label = label
      if (from.picture) from_ref.picture = from.picture
      // The sender is sent no `message` frame of its own: the row goes into the transcript here, at its real seq.
      const row: ProjectMessage = {
        message_id: msgId,
        cursor: seq,
        ts,
        body: text,
        from_ref,
        status: 'sent',
        mine: true,
        board: board.id,
        board_name: board.name,
        area: sentArea,
        project_id: projectId,
      }
      projectEcho(projectId, row)
      return row
    } catch (e) {
      const code = e instanceof Error ? e.message : String(e)
      if (code === 'attachment_invalid' && shot && attempt < 6) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
        continue
      }
      throw new Error(code)
    }
  }
}

/**
 * GET /conversations/{id}/members → {ok, members} like getProjectMembers. HUMANS ONLY —
 * agents come from the separate /agents route (the agents slice). A member maps to just
 * what the panel reads for a human row: email, label (the PROFILE# name), and `mine` for
 * the viewer's own row; no channel_hash/mode (those are the agent half).
 */
export async function dynamoGetProjectMembers(
  projectId: string,
): Promise<{ ok: boolean; members: ProjectMember[] }> {
  const self = chatv1SelfUser()
  // Humans and agents are separate rows server-side; the panel wants ONE list it splits
  // by `kind`. Fetch both; agents are best-effort so a roster read never hides the people.
  const [{ members }, agents] = await Promise.all([
    getMembers(projectId),
    dynamoGetProjectAgents(projectId).catch(() => [] as ProjectMember[]),
  ])
  const humans = members
    .filter((m) => m.status !== 'left')
    .map((m) => {
      const pm: ProjectMember = { user: m.user, mine: m.user === self }
      if (m.account) pm.account = m.account
      if (m.role) pm.role = m.role
      if (m.email) pm.email = m.email
      if (m.name) pm.label = m.name
      // The Google avatar rides the wire already (PROFILE# bridge resolves it);
      // dropping it here is why the Members sheet drew letter tiles (owner,
      // 2026-08-19: 把使用者的 Google Account 頭像放上去).
      if (m.picture) pm.picture = m.picture
      return pm
    })
  // WHOSE agent: `owner_user` is a chat v1 user id, never an email, so it is resolved
  // through the human rows here — the one place both halves are in hand. An agent whose
  // owner is in the room gets their email; one whose owner has left keeps only the id.
  // Before this the mapping kept `mine` alone, and every agent in the sheet read as
  // nobody's (owner, 2026-08-19: 把 Owner 的 Agents 放在 Owner 的下方 … 快速辨認 Agent 是誰的).
  const emailOfUser = new Map<string, string>()
  const labelOfUser = new Map<string, string>()
  for (const h of humans) {
    if (h.user && h.email) emailOfUser.set(h.user, h.email)
    // The PROFILE# name — what the busy float shows as WHOSE agent is working
    // (owner, 2026-08-26: 下標 Owner 的名稱，而非機器的名稱).
    if (h.user && h.label) labelOfUser.set(h.user, h.label)
  }
  const owned = agents.map((a) => {
    const email = a.owner_user ? emailOfUser.get(a.owner_user) : undefined
    const label = a.owner_user ? labelOfUser.get(a.owner_user) : undefined
    return {
      ...a,
      ...(email ? { owner_email: email } : {}),
      ...(label ? { owner_label: label } : {}),
    }
  })
  return { ok: true, members: [...humans, ...owned] }
}

/**
 * GET /conversations/{id}/agents → the attached agents, shaped as the `kind:'agent'`
 * ProjectMember rows the panel renders (channel_hash, label, mode, and `mine` for the
 * owner's own dial). `agent_hash` IS the edge channel hash the panel keys on.
 */
async function dynamoGetProjectAgents(projectId: string): Promise<ProjectMember[]> {
  const { agents } = await listAgents(projectId)
  const self = chatv1SelfUser()
  return agents.map((a) => ({
    kind: 'agent',
    channel_hash: a.agent_hash,
    label: a.name || a.agent_hash.slice(0, 8),
    mode: a.mode,
    owner_user: a.owner_user,
    mine: a.owner_user === self,
  }))
}

/** POST /conversations/{id}/agents — attach one of MY agents. `deviceId` is the machine it
 *  runs on (the feed routes the room's messages back to its pane). Own-agents only. */
export async function dynamoAttachProjectAgent(
  projectId: string,
  channelHash: string,
  deviceId: string,
  name: string,
): Promise<{ ok: boolean; error?: string }> {
  // The last gate before the wire: a deviceless AGENT# row is a poison record
  // (the feed cannot route to it, and its 400 once wedged a whole conversation's
  // stream batch). Whatever call site forgets the device in the future is
  // refused HERE with a name, not silently written.
  if (!deviceId) return { ok: false, error: 'no_device_for_agent' }
  try {
    await addAgent(projectId, channelHash, deviceId, name)
    return { ok: true }
  } catch (e) {
    return { ok: false, error: e instanceof Chatv1Error ? e.code : String(e) }
  }
}

/** DELETE /conversations/{id}/agents/{hash} — detach (any member may). */
export async function dynamoDetachProjectAgent(
  projectId: string,
  channelHash: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    await removeAgent(projectId, channelHash)
    return { ok: true }
  } catch (e) {
    return { ok: false, error: e instanceof Chatv1Error ? e.code : String(e) }
  }
}

/** PATCH /conversations/{id}/agents/{hash} — owner-only speak/listen/mute dial. */
export async function dynamoProjectAgentMode(
  projectId: string,
  channelHash: string,
  mode: AgentMode,
): Promise<{ ok: boolean; error?: string; mode?: AgentMode }> {
  try {
    await setAgentModeReq(projectId, channelHash, mode)
    return { ok: true, mode }
  } catch (e) {
    return { ok: false, error: e instanceof Chatv1Error ? e.code : String(e) }
  }
}

/**
 * The owner's attachable agents WITH the hosting device_id the chat attach needs — sourced
 * from `getAgentsEverywhere` (which stamps both the channel `hash` and its `device_id`),
 * minus the ones already on this project's roster. The edge's /project/attachable_agents
 * carries no device_id, so the DynamoDB path builds the list itself.
 */
/**
 * Fold one machine's attachable agents into what the sheet already shows.
 *
 * Keyed by (device, hash), because a hash is the session NAME hashed — two machines
 * each running `dev` share one hash, and keying on it alone would let the second
 * machine's row silently replace the first. Pure, so the dedupe is testable.
 */
export function mergeAttachable(
  prev: AttachableAgent[] | undefined,
  batch: AttachableAgent[],
): AttachableAgent[] {
  const out = [...(prev ?? [])]
  const seen = new Set(out.map((a) => `${a.device_id ?? ''} ${a.channel_hash}`))
  for (const a of batch) {
    const k = `${a.device_id ?? ''} ${a.channel_hash}`
    if (seen.has(k)) continue
    seen.add(k)
    out.push(a)
  }
  return out
}

export async function dynamoGetAttachableAgents(
  projectId: string,
  opts?: {
    /** Ask this machine first — the one whose agents the person most likely wants. */
    first?: string | null
    /** Called per machine, in arrival order, with that machine's attachables (already filtered). */
    onAgents?: (batch: AttachableAgent[]) => void
  },
): Promise<{ ok: boolean; agents: AttachableAgent[] }> {
  /*
   * BOTH READS START AT ONCE; BATCHES WAIT ONLY FOR THE FILTER.
   *
   * Owner, 2026-08-29: Project chat 想要 attach agent 等待 Agent list 的 Latency
   * 非常的高! 我等了十幾秒. The lump sweep was the ten seconds; fixed by per-machine
   * delivery. The FIRST version of that fix then serialized the attached-set read
   * (one chat-service call) in front of the sweep, and a cold chat Lambda put ~3s
   * back in front of the first row — measured on the served bundle, 850ms → 3828ms.
   *
   * So the sweep and the attached-set start together. A machine's batch cannot be
   * offered before the filter exists (it would flash agents already in the room),
   * so early batches BUFFER and flush the moment the set arrives — nothing waits
   * for anything it does not actually need.
   */
  let attachedSet: Set<string> | null = null
  const toAttachable = (
    list: Array<{
      hash: string
      session: string
      host: string | null
      device_id?: string
      device_name?: string
    }>,
  ): AttachableAgent[] =>
    list
      .filter((a) => a.hash && a.device_id && !attachedSet?.has(a.hash))
      .map((a) => ({
        channel_hash: a.hash,
        label: a.session,
        session: a.session,
        host: a.host,
        device_id: a.device_id!, // filtered truthy just above
        // The machine's NAME rides along or the sheet labels every row with the
        // relay's machine — machineNameOf's fallback chain ends there.
        ...(a.device_name ? { device_name: a.device_name } : {}),
      }))
  const attachedP = listAgents(projectId)
    .then((r) => new Set(r.agents.map((a) => a.agent_hash)))
    .catch(() => new Set<string>())
  type Raw = Parameters<typeof toAttachable>[0]
  const buffered: Raw[] = []
  const offer = (list: Raw): void => {
    const batch = toAttachable(list)
    if (batch.length) opts?.onAgents?.(batch)
  }
  const evP = getAgentsEverywhere(null, {
    first: opts?.first ?? null,
    onMachine: (m: RosterMachine) => {
      if (!opts?.onAgents) return
      if (attachedSet) offer(m.agents)
      else buffered.push(m.agents)
    },
  })
  attachedSet = await attachedP
  for (const b of buffered.splice(0)) offer(b)
  const ev = await evP
  return { ok: true, agents: toAttachable(ev.agents) }
}

/**
 * Share a file into a DynamoDB project. Presign → POST to S3 → reference the object in a
 * message (FileCard `body` for rendering + `envelope.attachments` for the claim). The
 * claim needs the object COMPLETED — an async S3 event flips pending→completed AFTER the
 * upload — so the send retries on `attachment_invalid` until it lands. Signature matches
 * the edge projectFileUpload so App's send path is unchanged.
 */
/**
 * How long an upload of `bytes` may stall before it is called a failure.
 *
 * A floor for the round trip plus an allowance per megabyte, sized for a slow
 * mobile uplink rather than a desk. The point is not to police speed — it is
 * that SOMETHING must eventually decide, because `fetch` on its own never will.
 */
export function s3UploadTimeoutMs(bytes: number): number {
  const FLOOR_MS = 30_000
  const PER_MB_MS = 20_000
  return FLOOR_MS + Math.ceil(bytes / (1024 * 1024)) * PER_MB_MS
}

/**
 * The bytes, to the presigned S3 POST a mint gave (a chat attachment, or a picture for a whiteboard — board/
 * boardImages.ts). The S3 policy requires the fields FIRST and the file part LAST. The deadline scales with the size
 * (s3UploadTimeoutMs, and the comment at its caller below says why there is one at all).
 */
export function postToS3(mint: UploadMint, body: Blob, filename?: string): Promise<Response> {
  const form = new FormData()
  for (const [k, v] of Object.entries(mint.post.fields)) form.append(k, v)
  if (filename) form.append('file', body, filename)
  else form.append('file', body)
  return fetch(mint.post.url, { method: 'POST', body: form, signal: AbortSignal.timeout(s3UploadTimeoutMs(body.size)) })
}

export async function dynamoProjectFileUpload(
  projectId: string,
  file: File,
  caption: string,
  onProgress?: (pct: number) => void,
  fromEmail?: string | null,
  fromName?: string | null,
  fromPicture?: string | null,
): Promise<{ ok?: boolean; error?: string; file_id?: string }> {
  const contentType = file.type || 'application/octet-stream'
  try {
    const mint = await mintUpload(projectId, file.size, contentType)
    onProgress?.(15)
    /*
     * THE OTHER PLACE AN IMAGE SEND COULD HANG FOREVER.
     *
     * Owner, 2026-09-18: 有時候圖+文的送出完全會被卡住. This POST carries the
     * actual bytes, so it is the await most likely to be in flight when a phone
     * walks out of coverage — and a bare `fetch` does not reject on a stalled
     * connection, it never settles. The caller's `finally` never ran, `sending`
     * stayed true, and the send button (disabled while sending) stayed dead with
     * the photo and its caption still in the composer.
     *
     * The deadline scales with the file: a fixed one would abort a large upload
     * that was progressing perfectly well on a slow link, which is the same bug
     * wearing the opposite sign.
     */
    const up = await postToS3(mint, file)
    if (!up.ok) return { ok: false, error: `s3_upload_${up.status}` }
    onProgress?.(70)

    const s3Key = mint.s3_key
    const card = {
      type: 'file' as const,
      ...(caption ? { text: caption } : {}),
      attachments: [{ file_id: s3Key, name: file.name, mime: contentType, size: file.size }],
    }
    const body = JSON.stringify(card)
    // The optimistic echo needs the SENDER's identity or its own file row renders as
    // "unknown / USER" (owner: 傳送者的解析沒有做好) — the server never echoes a message
    // frame back to the sender, so this is the only place the uploader's name/photo comes
    // from until a reconcile re-fetches the row enriched.
    const from_ref: { email?: string; label?: string; picture?: string } = {}
    if (fromEmail) from_ref.email = fromEmail
    const selfLabel = fromName || fromEmail
    if (selfLabel) from_ref.label = selfLabel
    if (fromPicture) from_ref.picture = fromPicture
    const envelope = {
      v: 1,
      mode: 'managed' as const,
      content_type: 'application/x-avengents-file',
      body,
      attachments: [{ s3_key: s3Key, size: file.size, content_type: contentType }],
    }
    // Claim race: retry the send until S3 has flipped the object to `completed`.
    let lastErr = 'attachment_invalid'
    for (let attempt = 0; attempt < 5; attempt++) {
      const msgId = crypto.randomUUID()
      try {
        const { seq, ts } = await sendChatv1Await({ action: 'send', conv_id: projectId, msg_id: msgId, envelope })
        onProgress?.(100)
        // The sender gets no `message` frame back (fanout skips its own socket), so echo
        // the card immediately, settled at its real seq.
        projectEcho(projectId, {
          message_id: msgId,
          cursor: seq,
          ts,
          body,
          from_ref,
          status: 'sent',
          project_id: projectId,
        })
        return { ok: true, file_id: s3Key }
      } catch (e) {
        lastErr = e instanceof Error ? e.message : String(e)
        if (lastErr === 'attachment_invalid' && attempt < 4) {
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
          continue
        }
        return { ok: false, error: lastErr }
      }
    }
    return { ok: false, error: lastErr }
  } catch (e) {
    /*
     * A DEADLINE MUST EXPLAIN ITSELF. `AbortSignal.timeout` rejects with a bare
     * "signal timed out", which tells the person nothing about what to do — and
     * this message now stands in for a hang they previously could not escape at
     * all, so it is the whole of what they learn. Name the cause, and say the
     * file is still there, because it is: staged items survive a failed send.
     */
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      return {
        ok: false,
        error: 'the upload stalled and was given up on — your file is still here, try again',
      }
    }
    return { ok: false, error: e instanceof Chatv1Error ? e.code : String(e) }
  }
}

/** Bytes of a claimed attachment: presign a download, then fetch the S3 object (bucket
 *  CORS lets the browser read it into a Blob). Feeds AttachmentCard's preview/download.
 *  Held as a type that cannot run, whatever the uploader called it (ui/inertBlob.ts). */
export async function dynamoAttachmentBytes(_projectId: string, fileId: string): Promise<Blob> {
  const { url } = await attachmentUrl(fileId)
  const res = await fetch(url)
  if (!res.ok) throw new Chatv1Error(res.status, 'download_failed')
  return inertBlob(await res.blob())
}

/**
 * POST /conversations/{id}/members {member} — add-human. The owner invites a friend by
 * email; the server EMAILIDX-resolves it to an account. Errors come back as the backend's
 * code (e.g. `not_friends`, `unknown_peer`) so the panel can show something actionable.
 */
export async function dynamoProjectInvite(
  projectId: string,
  toEmail: string,
  account?: string,
): Promise<{ ok?: boolean; error?: string }> {
  try {
    await addMember(projectId, toEmail, account)
    return { ok: true }
  } catch (e) {
    return { ok: false, error: e instanceof Chatv1Error ? e.code : String(e) }
  }
}

/** DELETE /conversations/{id}/members/me — leave the project. */
export async function dynamoProjectLeave(
  projectId: string,
): Promise<{ ok?: boolean; error?: string }> {
  try {
    await leaveConversation(projectId)
    return { ok: true }
  } catch (e) {
    return { ok: false, error: e instanceof Chatv1Error ? e.code : String(e) }
  }
}
